import { useContext, useEffect, useMemo, type MouseEvent } from 'react'
import {
  Breadcrumb,
  Card,
  Col,
  Empty,
  Input,
  Menu,
  Result,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd'
import type { MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { BaseLayoutContext } from '../BaseLayoutContext'
import { useApiQuery } from '../../lib/api'
import { gutter } from '../../lib/layout'
import { CollectionIcon } from './HelpIcons'
import {
  HELP_ROOT,
  PORTAL_COLLECTION_ID,
  articlesWithoutCategory,
  buildCategoryTree,
  categoryTrail,
  findCategory,
  findLegacyArticle,
  findLegacyCollection,
  helpArticlePath,
  helpCategoryPath,
  helpLocale,
  helpPortalPath,
  helpSearchPath,
  highlightParts,
  type HelpCategoryNode,
} from './helpTree'
import { renderHelpArticle, type HelpTocEntry } from './renderHelpMarkdown'
import type {
  HelpArticle,
  HelpArticleSummary,
  HelpIndex,
  HelpPortalIndex,
  HelpPortalKey,
  HelpSearchResult,
} from './types'
import styles from './HelpCenter.module.less'

const EMPTY_INDEX: HelpIndex = { portals: [] }
const NO_ARTICLE: { article: HelpArticle | null; requestedId?: number } = {
  article: null,
}
const NO_RESULTS: { results: HelpSearchResult[] } = { results: [] }
const MIN_QUERY_LENGTH = 2

function isPortalKey(value: string | undefined): value is HelpPortalKey {
  return value === 'customer' || value === 'internal'
}

function InternalTag() {
  const { t } = useTranslation()
  return <Tag color="gold">{t('frontend:helpCenter.internal')}</Tag>
}

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, position) =>
        part.match ? (
          <mark key={position} className={styles.match}>
            {part.text}
          </mark>
        ) : (
          <span key={position}>{part.text}</span>
        )
      )}
    </>
  )
}

function ArticleLinks({
  portal,
  articles,
}: {
  portal: HelpPortalKey
  articles: HelpArticleSummary[]
}) {
  return (
    <>
      {articles.map((article) => (
        <Link
          key={article.id}
          to={helpArticlePath(portal, article.id)}
          className={styles.articleLink}
        >
          {article.title}
        </Link>
      ))}
    </>
  )
}

function CategorySection({
  portal,
  node,
  level,
}: {
  portal: HelpPortalKey
  node: HelpCategoryNode
  level: 4 | 5
}) {
  return (
    <div>
      <Typography.Title level={level} style={{ marginTop: 16 }}>
        <Link to={helpCategoryPath(portal, node.id)}>{node.name}</Link>
      </Typography.Title>
      <ArticleLinks portal={portal} articles={node.articles} />
      {node.children.map((child) => (
        <CategorySection
          key={child.id}
          portal={portal}
          node={child}
          level={5}
        />
      ))}
    </div>
  )
}

// One collection on the start page: what it is about and how much is in it.
// The articles themselves are on the collection's own page.
function CollectionCard({
  to,
  icon,
  name,
  description,
  count,
  internal,
}: {
  to: string
  icon: string | null
  name: string
  description: string | null
  count: number
  internal?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Link to={to} className={styles.collectionLink}>
      <Card bordered={false} hoverable className={styles.collectionCard}>
        <CollectionIcon icon={icon} className={styles.collectionIcon} />
        <div className={styles.collectionName}>
          {name} {internal && <InternalTag />}
        </div>
        {description && (
          <div className={styles.collectionDescription}>{description}</div>
        )}
        <div className={styles.collectionCount}>
          {t('frontend:helpCenter.articleCount', { count })}
        </div>
      </Card>
    </Link>
  )
}

function Overview({ index }: { index: HelpIndex }) {
  const { t } = useTranslation()
  const customer = index.portals.find((entry) => entry.portal === 'customer')
  const internal = index.portals.find((entry) => entry.portal === 'internal')
  const tree = useMemo(
    () => (customer ? buildCategoryTree(customer) : []),
    [customer]
  )
  const loose = useMemo(
    () => (customer ? articlesWithoutCategory(customer) : []),
    [customer]
  )
  const internalCount = internal?.articles.length ?? 0
  if (tree.length === 0 && loose.length === 0 && internalCount === 0) {
    return (
      <Card bordered={false}>
        <Empty description={t('frontend:helpCenter.empty')} />
      </Card>
    )
  }
  return (
    <Row gutter={gutter}>
      {tree.map((node) => (
        <Col key={node.id} xs={24} xl={12} className={styles.collectionCol}>
          <CollectionCard
            to={helpCategoryPath('customer', node.id)}
            icon={node.icon}
            name={node.name}
            description={node.description}
            count={node.articleCount}
          />
        </Col>
      ))}
      {internalCount > 0 && (
        <Col xs={24} xl={12} className={styles.collectionCol}>
          <CollectionCard
            to={helpPortalPath('internal')}
            icon="folder"
            name={t('frontend:helpCenter.internalTitle')}
            description={null}
            count={internalCount}
            internal
          />
        </Col>
      )}
      {loose.length > 0 && (
        <Col xs={24}>
          <Card
            bordered={false}
            title={t('frontend:helpCenter.moreArticles')}
            style={{ marginBottom: 16 }}
          >
            <ArticleLinks portal="customer" articles={loose} />
          </Card>
        </Col>
      )}
    </Row>
  )
}

function CategoryView({ index }: { index: HelpIndex }) {
  const { t } = useTranslation()
  const { portal: portalParam, categoryId } = useParams()
  const portal = index.portals.find((entry) => entry.portal === portalParam)
  const tree = useMemo(
    () => (portal ? buildCategoryTree(portal) : []),
    [portal]
  )
  const loose = useMemo(
    () => (portal ? articlesWithoutCategory(portal) : []),
    [portal]
  )
  // The internal portal as a whole is one collection.
  const wholePortal =
    portal?.portal === 'internal' && categoryId === PORTAL_COLLECTION_ID
  const node = wholePortal ? undefined : findCategory(tree, Number(categoryId))
  if (!portal || (!node && !wholePortal)) return <NotFound />
  if (wholePortal && portal.articles.length === 0) return <NotFound />

  const internal = portal.portal === 'internal'
  const trail = node ? categoryTrail(tree, node.id) : []
  const name = node ? node.name : t('frontend:helpCenter.internalTitle')
  const count = node ? node.articleCount : portal.articles.length
  return (
    <Card bordered={false}>
      <Breadcrumb
        items={[
          {
            title: <Link to={HELP_ROOT}>{t('frontend:helpCenter.title')}</Link>,
          },
          ...(internal && node
            ? [
                {
                  title: (
                    <Link to={helpPortalPath('internal')}>
                      {t('frontend:helpCenter.internalTitle')}
                    </Link>
                  ),
                },
              ]
            : []),
          ...trail.slice(0, -1).map((entry) => ({
            title: (
              <Link to={helpCategoryPath(portal.portal, entry.id)}>
                {entry.name}
              </Link>
            ),
          })),
          { title: name },
        ]}
      />
      <div className={styles.collectionHead}>
        <CollectionIcon
          icon={node ? node.icon : 'folder'}
          className={styles.collectionIcon}
        />
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {name} {internal && <InternalTag />}
          </Typography.Title>
          {node?.description && (
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {node.description}
            </Typography.Paragraph>
          )}
          <Typography.Text type="secondary">
            {t('frontend:helpCenter.articleCount', { count })}
          </Typography.Text>
        </div>
      </div>
      <ArticleLinks
        portal={portal.portal}
        articles={node ? node.articles : loose}
      />
      {(node ? node.children : tree).map((child) => (
        <CategorySection
          key={child.id}
          portal={portal.portal}
          node={child}
          level={4}
        />
      ))}
    </Card>
  )
}

function NotFound() {
  const { t } = useTranslation()
  return (
    <Card bordered={false}>
      <Result
        status="404"
        title={t('frontend:helpCenter.notFound')}
        extra={
          <Link to={HELP_ROOT}>{t('frontend:helpCenter.backToOverview')}</Link>
        }
      />
    </Card>
  )
}

// Links written for the old help center arrive here with the old id; the
// article or collection that replaced it is looked up in the current index.
function LegacyArticle({ index }: { index: HelpIndex }) {
  const { legacyId = '' } = useParams()
  const found = findLegacyArticle(legacyId, index)
  if (!found) return <NotFound />
  return <Navigate to={helpArticlePath(found.portal, found.id)} replace />
}

function LegacyCollection({ index }: { index: HelpIndex }) {
  const { legacyId = '' } = useParams()
  const found = findLegacyCollection(legacyId, index)
  return (
    <Navigate
      to={found ? helpCategoryPath(found.portal, found.id) : HELP_ROOT}
      replace
    />
  )
}

function TableOfContents({ toc }: { toc: HelpTocEntry[] }) {
  const { t } = useTranslation()
  if (toc.length < 2) return null
  return (
    <nav
      className={styles.toc}
      aria-label={t('frontend:helpCenter.tableOfContents')}
    >
      <div className={styles.tocTitle}>
        {t('frontend:helpCenter.tableOfContents')}
      </div>
      {toc.map((entry) => (
        <a
          key={entry.id}
          href={`#${entry.id}`}
          className={styles.tocEntry}
          onClick={(event) => {
            event.preventDefault()
            document
              .getElementById(entry.id)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        >
          {entry.text}
        </a>
      ))}
    </nav>
  )
}

function ArticleView({ index, locale }: { index: HelpIndex; locale: string }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { portal: portalParam, articleId } = useParams()
  const id = Number(articleId)
  const valid = isPortalKey(portalParam) && Number.isInteger(id) && id > 0
  const request = useMemo(
    () => ({ portal: portalParam, articleId: id, locale }),
    [portalParam, id, locale]
  )
  const { data, status } = useApiQuery<typeof request, typeof NO_ARTICLE>(
    'chatwoot/help/get_article',
    { data: request, startWithValue: NO_ARTICLE, enabled: valid }
  )
  const portal = index.portals.find((entry) => entry.portal === portalParam)
  const tree = useMemo(
    () => (portal ? buildCategoryTree(portal) : []),
    [portal]
  )
  // A previous article stays in `data` while the next one loads.
  // The id in the address can be the article in another language (after a
  // language switch); the answer is then the one for the current language.
  const article =
    data.article &&
    (data.article.id === id || data.requestedId === id) &&
    data.article.portal === portalParam
      ? data.article
      : null
  const canonicalId = article?.id
  useEffect(() => {
    if (canonicalId != null && canonicalId !== id && isPortalKey(portalParam)) {
      navigate(helpArticlePath(portalParam, canonicalId), { replace: true })
    }
  }, [canonicalId, id, portalParam, navigate])
  const rendered = useMemo(
    () => (article ? renderHelpArticle(article.content, index) : null),
    [article, index]
  )

  if (!valid || status === 'error') return <NotFound />
  if (!article || !rendered) {
    return (
      <Card bordered={false}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  // Links between help articles stay inside the app instead of reloading it.
  function onBodyClick(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const anchor = (event.target as HTMLElement).closest?.('a[data-in-app]')
    const href = anchor?.getAttribute('href')
    if (!href) return
    event.preventDefault()
    navigate(href)
  }

  const internal = article.portal === 'internal'
  const trail = categoryTrail(tree, article.categoryId)
  return (
    <Card bordered={false}>
      <Breadcrumb
        items={[
          {
            title: <Link to={HELP_ROOT}>{t('frontend:helpCenter.title')}</Link>,
          },
          ...(internal
            ? [
                {
                  title: (
                    <Link to={helpPortalPath('internal')}>
                      {t('frontend:helpCenter.internalTitle')}
                    </Link>
                  ),
                },
              ]
            : []),
          ...trail.map((entry) => ({
            title: (
              <Link to={helpCategoryPath(article.portal, entry.id)}>
                {entry.name}
              </Link>
            ),
          })),
        ]}
      />
      <div className={styles.articleLayout}>
        <div className={styles.articleColumn}>
          <Typography.Title level={2} style={{ marginTop: 16 }}>
            {article.title} {internal && <InternalTag />}
          </Typography.Title>
          {article.updatedAt && (
            <Typography.Paragraph type="secondary">
              {t('frontend:helpCenter.updatedAt', {
                date: new Date(article.updatedAt).toLocaleDateString(
                  i18n.language
                ),
              })}
            </Typography.Paragraph>
          )}
          <div
            className={styles.articleBody}
            onClick={onBodyClick}
            dangerouslySetInnerHTML={{ __html: rendered.html }}
          />
        </div>
        <TableOfContents toc={rendered.toc} />
      </div>
    </Card>
  )
}

function SearchResults({ query, locale }: { query: string; locale: string }) {
  const { t } = useTranslation()
  const request = useMemo(() => ({ query, locale }), [query, locale])
  const { data, status } = useApiQuery<typeof request, typeof NO_RESULTS>(
    'chatwoot/help/search',
    { data: request, startWithValue: NO_RESULTS }
  )
  return (
    <Card
      bordered={false}
      title={t('frontend:helpCenter.resultsFor', { query })}
    >
      {status === 'loading' ? (
        <Skeleton active />
      ) : data.results.length === 0 ? (
        <Empty description={t('frontend:helpCenter.noResults')} />
      ) : (
        data.results.map((result) => (
          <Link
            key={`${result.portal}:${result.id}`}
            to={helpArticlePath(result.portal, result.id)}
            className={styles.articleLink}
          >
            <Highlighted text={result.title} query={query} />{' '}
            {result.portal === 'internal' && <InternalTag />}
            <span className={styles.snippet}>
              <Highlighted text={result.snippet} query={query} />
            </span>
          </Link>
        ))
      )}
    </Card>
  )
}

function categoryMenuItems(
  portal: HelpPortalKey,
  nodes: HelpCategoryNode[]
): NonNullable<MenuProps['items']> {
  return nodes.map((node) => ({
    key: helpCategoryPath(portal, node.id),
    label: node.name,
    children: node.children.length
      ? [
          {
            key: `${helpCategoryPath(portal, node.id)}#all`,
            label: node.name,
          },
          ...categoryMenuItems(portal, node.children),
        ]
      : undefined,
  }))
}

function menuItemsFor(
  portal: HelpPortalIndex,
  internalTitle: string
): NonNullable<MenuProps['items']> {
  const tree = buildCategoryTree(portal)
  if (tree.length === 0) return []
  if (portal.portal !== 'internal') {
    return categoryMenuItems(portal.portal, tree)
  }
  return [
    {
      type: 'group',
      key: 'internal',
      label: internalTitle,
      children: categoryMenuItems(portal.portal, tree),
    },
  ]
}

export function HelpCenterPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [, setTopMenu] = useContext(BaseLayoutContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const locale = helpLocale(i18n.language)
  const query = (searchParams.get('q') ?? '').trim()

  useEffect(() => {
    setTopMenu({ selectedProfile: null, menu: [], homeLink: HELP_ROOT })
  }, [setTopMenu])

  const request = useMemo(() => ({ locale }), [locale])
  const { data: index, status } = useApiQuery<typeof request, HelpIndex>(
    'chatwoot/help/get_index',
    { data: request, startWithValue: EMPTY_INDEX }
  )

  const menuItems = useMemo(
    () =>
      index.portals.flatMap((portal) =>
        menuItemsFor(portal, t('frontend:helpCenter.internalTitle'))
      ),
    [index, t]
  )

  if (status === 'error') {
    return (
      <Card bordered={false}>
        <Result status="warning" title={t('frontend:helpCenter.loadFailed')} />
      </Card>
    )
  }

  return (
    <Row gutter={gutter}>
      <Col xs={24} lg={7} xl={6}>
        <Card bordered={false} styles={{ body: { padding: 12 } }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Title level={4} style={{ margin: 0 }}>
              <Link to={HELP_ROOT}>{t('frontend:helpCenter.title')}</Link>
            </Typography.Title>
            <Input.Search
              allowClear
              defaultValue={query}
              key={query}
              placeholder={t('frontend:helpCenter.searchPlaceholder')}
              onSearch={(value) => {
                const next = value.trim()
                if (next.length >= MIN_QUERY_LENGTH) {
                  navigate(helpSearchPath(next))
                } else if (query) {
                  setSearchParams({})
                }
              }}
            />
            <Menu
              mode="inline"
              selectable={false}
              items={menuItems}
              style={{ borderInlineEnd: 0 }}
              onClick={({ key }) => navigate(key.replace(/#all$/, ''))}
            />
          </Space>
        </Card>
      </Col>
      <Col xs={24} lg={17} xl={18}>
        {status === 'loading' && index === EMPTY_INDEX ? (
          <Card bordered={false}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        ) : query.length >= MIN_QUERY_LENGTH ? (
          <SearchResults query={query} locale={locale} />
        ) : (
          <Routes>
            <Route index element={<Overview index={index} />} />
            <Route
              path="c/:portal/:categoryId"
              element={<CategoryView index={index} />}
            />
            <Route
              path="a/:portal/:articleId"
              element={<ArticleView index={index} locale={locale} />}
            />
            <Route
              path="l/c/:legacyId"
              element={<LegacyCollection index={index} />}
            />
            <Route
              path="l/:legacyId"
              element={<LegacyArticle index={index} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </Col>
    </Row>
  )
}
